import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { getObjectId } from '@/helpers/object.id.helper';
import { usePublicationStateQuery } from '@/state/queries/events/use-publication-state';
import { PUBLICATION_ITEM_TYPE } from '@/types/dtos/events/request/batch-publish.dto';

export type PublicationStatePhase = 'disabled' | 'loading' | 'error' | 'ready';

export type FlowPublishStateForDetail = {
  publicationPhase: PublicationStatePhase;
  isUpToDate: boolean;
  needsInitialPublish: boolean;
  needsRepublish: boolean;
  /** Flow sequence & slot timing vs ORGANIZE (`FLOW_SLOTS` publication row `isUpToDate`). */
  flowUpToDate: boolean;
  /** Sessions in the flow: `SESSION_BASIC` rows `isUpToDate`. */
  sessionsInFlowUpToDate: boolean;
  /** Event slot (day/time): `EVENT_SLOT` publication row `isUpToDate`. True when no event-slot context. */
  eventSlotUpToDate: boolean;
  /** Retained for type compatibility; not used for flow schedule publish. */
  eventUpToDate: boolean;
};

/**
 * Publish state for the flow schedule tab: `FLOW_SLOTS` + `sessionBasic` for sessions in slots only.
 * Staleness from GET publication-state only when `publicationPhase === 'ready'` (no publishedModes / version).
 */
export function useFlowPublishStateForFlowDetail(flowPlanOverride?: FlowDto | null): FlowPublishStateForDetail | null {
  const [searchParams] = useSearchParams();
  const eventSlotIdParam = searchParams.get('eventSlotId');
  const flowFromStore = useFlowStore((s) => s.flow);
  const flowPlanFromStore = useFlowStore((s) => s.flowPlan);
  const event = useEventStore((s) => s.event);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);
  const { data: permissions } = usePermissionsQuery();

  const flowToUse = flowFromStore;
  const flowPlan = flowPlanOverride ?? flowPlanFromStore;

  const eventIdFromFlowParent =
    flowToUse?.parent && typeof flowToUse.parent === 'object' && 'id' in flowToUse.parent
      ? flowToUse.parent.id
      : undefined;
  const eventId = eventPlan?.id ?? event?.id ?? eventIdFromFlowParent;
  const canUpdateEvent = !!eventId && !!permissions?.events?.[eventId]?.includes('update');

  const flowForPublish = useMemo(() => {
    if (!flowPlan || !flowToUse) return null;
    const planOriginId = flowPlan.origin ? getObjectId(flowPlan.origin) : flowPlan.id;
    const flowOriginId = flowToUse.origin ? getObjectId(flowToUse.origin) : flowToUse.id;
    return planOriginId === flowOriginId ? flowPlan : null;
  }, [flowPlan, flowToUse]);

  const planFlowId = flowForPublish?.id ?? null;

  const publicationQuery = usePublicationStateQuery({
    eventId,
    mode: 'ORGANIZE',
    enabled: canUpdateEvent,
  });

  const publicationPhase: PublicationStatePhase = useMemo(() => {
    if (!canUpdateEvent) return 'disabled';
    if (publicationQuery.isError) return 'error';
    if (publicationQuery.isPublicationStateReady) return 'ready';
    return 'loading';
  }, [canUpdateEvent, publicationQuery.isError, publicationQuery.isPublicationStateReady]);

  const sessionsInFlow = useMemo(() => {
    if (!flowPlan || !flowToUse || !flowForPublish) return [];
    const slots = flowForPublish.slots ?? [];
    const resolveSessionBySlot = (slot: FlowDto['slots'][number]): SessionDto | undefined => {
      if (slot.contentType !== 'Session' || !slot.content) return undefined;
      if (typeof slot.content === 'object') return slot.content as SessionDto;
      const slotContentId = slot.content;
      const planSession = eventSessionsPlan?.find((s) => s.id === slotContentId);
      const organizeSession = eventSessionsOrganize?.find((s) => s.id === slotContentId);
      return planSession ?? organizeSession;
    };
    const sessions = slots.map((slot) => resolveSessionBySlot(slot)).filter(Boolean) as SessionDto[];
    const resolvePublishSession = (session: SessionDto) => {
      const originId = session.origin ? getObjectId(session.origin) : session.id;
      const planSession = eventSessionsPlan?.find((s) => {
        const planOriginId = s.origin ? getObjectId(s.origin) : s.id;
        return planOriginId === originId || s.id === originId;
      });
      return planSession ?? session;
    };
    return sessions.map(resolvePublishSession);
  }, [flowPlan, flowToUse, flowForPublish, eventSessionsPlan, eventSessionsOrganize]);

  const uniqueSessionsInFlow = useMemo(() => {
    const byId = new Map<string, SessionDto>();
    for (const s of sessionsInFlow) {
      byId.set(s.id, s);
    }
    return [...byId.values()];
  }, [sessionsInFlow]);

  const flowSlotsRecord = useMemo(() => {
    if (!publicationQuery.data || !planFlowId) return undefined;
    return publicationQuery.data.items.find(
      (i) => i.itemType === PUBLICATION_ITEM_TYPE.FLOW_SLOTS && i.scopeKey === planFlowId,
    );
  }, [publicationQuery.data, planFlowId]);

  const flowUpToDate = useMemo(() => {
    if (!flowForPublish || !planFlowId) return false;
    if (publicationPhase !== 'ready') return false;
    return flowSlotsRecord?.isUpToDate === true;
  }, [flowForPublish, planFlowId, publicationPhase, flowSlotsRecord]);

  const eventSlotForPublish = useMemo(() => {
    if (!eventSlotIdParam) return null;
    const flowSlot = flowForPublish?.slot;
    if (flowSlot) {
      const originId = flowSlot.origin ? getObjectId(flowSlot.origin) : undefined;
      const matchesUrl =
        flowSlot.id === eventSlotIdParam || flowSlot.origin === eventSlotIdParam || originId === eventSlotIdParam;
      if (matchesUrl) return flowSlot;
    }
    const matchSlot = (slotId?: string) =>
      slotId ? eventPlan?.slots?.find((slot) => slot.id === slotId || slot.origin === slotId) : undefined;
    return matchSlot(eventSlotIdParam) ?? matchSlot(flowForPublish?.slot?.id) ?? null;
  }, [eventSlotIdParam, flowForPublish?.slot, eventPlan?.slots]);

  const resolvedPlanEventIdForScope = publicationQuery.data?.planEventId ?? eventId;

  const eventSlotRecord = useMemo(() => {
    if (!publicationQuery.data || !resolvedPlanEventIdForScope || !eventSlotForPublish) return undefined;
    const sk = `${resolvedPlanEventIdForScope}:eventSlot:${eventSlotForPublish.id}`;
    return publicationQuery.data.items.find(
      (i) => i.itemType === PUBLICATION_ITEM_TYPE.EVENT_SLOT && i.scopeKey === sk,
    );
  }, [publicationQuery.data, resolvedPlanEventIdForScope, eventSlotForPublish]);

  const eventSlotUpToDate = useMemo(() => {
    if (!eventSlotForPublish) return true;
    if (publicationPhase !== 'ready') return false;
    return eventSlotRecord?.isUpToDate === true;
  }, [eventSlotForPublish, publicationPhase, eventSlotRecord]);

  const sessionsInFlowUpToDate = useMemo(() => {
    if (!canUpdateEvent) return true;
    if (publicationPhase !== 'ready' || !publicationQuery.data) return true;
    if (!uniqueSessionsInFlow.length) return true;
    return uniqueSessionsInFlow.every((session) => {
      const r = publicationQuery.data!.items.find(
        (i) => i.itemType === PUBLICATION_ITEM_TYPE.SESSION_BASIC && i.scopeKey === session.id,
      );
      return r?.isUpToDate === true;
    });
  }, [canUpdateEvent, publicationPhase, publicationQuery.data, uniqueSessionsInFlow]);

  const publishState = useMemo(() => {
    if (!flowPlan || !flowForPublish) {
      return {
        publicationPhase: 'disabled' as PublicationStatePhase,
        isUpToDate: false,
        needsInitialPublish: true,
        needsRepublish: false,
        flowUpToDate: false,
        sessionsInFlowUpToDate: !canUpdateEvent,
        eventSlotUpToDate: true,
        eventUpToDate: true,
      };
    }

    const needsInitialPublish = publicationPhase === 'ready' ? !flowSlotsRecord && !eventSlotRecord : false;
    const isUpToDate = flowUpToDate && sessionsInFlowUpToDate && eventSlotUpToDate;
    const needsRepublish = publicationPhase === 'ready' && !needsInitialPublish && !isUpToDate;

    return {
      publicationPhase,
      isUpToDate,
      needsInitialPublish,
      needsRepublish,
      flowUpToDate,
      sessionsInFlowUpToDate,
      eventSlotUpToDate,
      eventUpToDate: true,
    };
  }, [
    flowPlan,
    flowForPublish,
    publicationPhase,
    flowSlotsRecord,
    eventSlotRecord,
    flowUpToDate,
    sessionsInFlowUpToDate,
    eventSlotUpToDate,
    canUpdateEvent,
  ]);

  const planMatchesFlow = useMemo(() => {
    if (!flowPlan || !flowToUse) return false;
    const planOriginId = flowPlan.origin ? getObjectId(flowPlan.origin) : flowPlan.id;
    const flowOriginId = flowToUse.origin ? getObjectId(flowToUse.origin) : flowToUse.id;
    return planOriginId === flowOriginId;
  }, [flowPlan, flowToUse]);

  if (!flowToUse || !flowPlan || !planMatchesFlow) {
    return null;
  }

  return publishState;
}
